import * as React from 'react';
import { StyleSheet, css } from 'aphrodite/no-important';
import { TypographySize } from 'mm-theme-configuration/dist/consts';
import { Link } from '../../../../partials/link/Link';
import { useTheme } from '../../../../../theming/useTheme';
import { BaseUnit } from '../../../../../theming/baseUnitDefinition';
import { getLinksStyle } from './linkStyle.utils';
import { Span } from '../../../../../typography/semanticTags';
import { TranslatedFontSize } from '../../../../../theming/fontSizeTranslator';
import { getStyleBySize } from '../../../../../typography/semanticTags/semanticTags.utils';
import { LinkWithSubMenu } from './LinkWithSubMenu';
import { useSubMenu } from './subMenuReducer';
import { NavigationLink } from '../primaryHeader/PrimaryHeader';
import { AnimationDirection, AnimationDirectionType } from '../menuAnimations.util';
import { reducedHeaderFirstRowHeightFactors } from '../header.utils';

export const componentConfigThemeKey = 'subMenu';

const MAX_NUM_ROWS = 7;

interface SubMenuProps {
  links: NavigationLink[];
  parentElement: HTMLElement;
  activeStateColor: string;
  hoverStateColor: string;
  idleColor: string;
  pagePath: string;
  linkFontSizeLarge: TypographySize;
  linkFontSizeMedium: TypographySize;
  linkFontSizeSmall: TypographySize;
  backgroundColor: string;
  shouldOffsetLeft: boolean;
  subMenuLinksSecondLevelBackgroundColor?: string;
  menuFoldState: AnimationDirectionType;
  animationDuration: number;
}

interface SubMenuThemeProps {
  baseUnit: BaseUnit;
  fontSizesStyles: TranslatedFontSize;
  innerMenuFontSizeLarge: TypographySize;
}

const getStyles = (backgroundColor: string, baseUnit: BaseUnit, offset: number, numMenuRows: number, minWidth: number, shouldOffsetLeft: boolean, animationDuration: number, menuFoldState: AnimationDirectionType) => {
  return StyleSheet.create({
    ul: {
      position: 'absolute',
      left: `${offset - baseUnit / 2}px`,
      transition: `top ${animationDuration}ms ease 0s`,
      top: menuFoldState === AnimationDirection.UP ? `${baseUnit * 2 * reducedHeaderFirstRowHeightFactors.large}px` : `${baseUnit * 2}px`,
      backgroundColor,
      listStyle: 'none',
      padding: `${baseUnit / 2}px`,
      display: 'grid',
      gridRowGap: `${baseUnit / 2}px`,
      gridColumnGap: `${baseUnit * 4}px`,
      gridAutoFlow: 'column',
      gridTemplateRows: `repeat(${numMenuRows}, 1fr)`,
      minWidth: `${minWidth}px`,
      transform: shouldOffsetLeft ? 'translateX(-100%)' : '',
    },
    li: {
      padding: 0,
      position: 'relative',
    },
    link: {
      whiteSpace: 'nowrap',
      cursor: 'pointer',
    },
  });
};

const getOpenListItemStyle = (isSubMenuOpen: boolean, activeMenuBackgroundColor: string, baseUnit: BaseUnit) => {
  if (!isSubMenuOpen) return null;

  return StyleSheet.create({
    li: {
      position: 'relative',
      ':before': {
        content: isSubMenuOpen ? "''" : '',
        backgroundColor: activeMenuBackgroundColor,
        position: 'absolute',
        top: `-${baseUnit / 2}px`,
        left: `-${baseUnit / 2}px`,
        bottom: `-${baseUnit / 2}px`,
        right: `-${baseUnit / 2}px`,
        zIndex: 0,
      },
    },
  }).li;
};

const getMenuOffset = (parentElement: HTMLElement) => {
  const offset = parentElement.getBoundingClientRect().left - parentElement.parentElement!.parentElement!.getBoundingClientRect().left;
  return offset;
};

const getMenuMinWidth = (parentElement: HTMLElement) => {
  return parentElement.getBoundingClientRect().width;
};

const getWrapperStyle = (element: HTMLElement, baseUnit: BaseUnit) => {
  const elementLeft = element.getBoundingClientRect().left;
  const elementTop = element.parentElement!.getBoundingClientRect().top;
  const elementParentTop = element.parentElement!.parentElement!.getBoundingClientRect().top;
  return StyleSheet.create({
    subMenuWrapper: {
      position: 'absolute',
      left: `${elementLeft - baseUnit / 2 + 1}px`,
      top: `${elementTop - elementParentTop - baseUnit / 2}px`,
    },
  });
};

export const SubMenu: React.FunctionComponent<SubMenuProps> = props => {
  const { links, parentElement, activeStateColor, hoverStateColor, idleColor, linkFontSizeLarge, linkFontSizeMedium, linkFontSizeSmall, backgroundColor, pagePath, shouldOffsetLeft, subMenuLinksSecondLevelBackgroundColor, animationDuration, menuFoldState } = props;
  const { baseUnit, fontSizesStyles, innerMenuFontSizeLarge } = useTheme<SubMenuThemeProps>(componentConfigThemeKey);
  const [subMenuState, dispatch] = useSubMenu();
  const offset = getMenuOffset(parentElement);
  const numMenuRows = Math.min(links.length, MAX_NUM_ROWS);
  const minWidth = getMenuMinWidth(parentElement);
  const styles = getStyles(backgroundColor, baseUnit, offset, numMenuRows, minWidth, shouldOffsetLeft, animationDuration, menuFoldState);
  const linkStyle = getLinksStyle(activeStateColor, hoverStateColor, idleColor, false);
  const fontStyle = getStyleBySize(fontSizesStyles, { fontSizeSmall: linkFontSizeSmall, fontSizeMedium: linkFontSizeMedium, fontSizeLarge: linkFontSizeLarge });
  const isMenuOpen = subMenuState.index >= 0;

  const onClickLinkWithSubMenu = (element: HTMLElement, index: number) => {
    const menuLinks = links![index].children;
    dispatch({
      data: {
        index,
        links: menuLinks,
        element,
        elementOffset: 0,
      },
    });
  };

  return (
    <React.Fragment>
      {
        isMenuOpen && (
          <div className={css(getWrapperStyle(subMenuState.element!, baseUnit).subMenuWrapper)}>
            <SubMenu
              activeStateColor={activeStateColor}
              hoverStateColor={hoverStateColor}
              idleColor={idleColor}
              backgroundColor={subMenuLinksSecondLevelBackgroundColor || backgroundColor}
              pagePath={pagePath}
              links={subMenuState.links!}
              parentElement={subMenuState.element!}
              linkFontSizeLarge={innerMenuFontSizeLarge}
              linkFontSizeMedium={linkFontSizeMedium}
              linkFontSizeSmall={linkFontSizeSmall}
              menuFoldState={menuFoldState}
              animationDuration={animationDuration}
              shouldOffsetLeft
            />
          </div>
        )
      }
      <ul className={css(styles.ul)}>
        {
          links.map((navigationLink, index) => {
            return (
              <li key={index} className={css(getOpenListItemStyle(subMenuState.index === index, subMenuLinksSecondLevelBackgroundColor || backgroundColor, baseUnit))}>
                {
                  navigationLink.children && navigationLink.children.length > 0 ? (
                    <LinkWithSubMenu
                      title={navigationLink.link.text}
                      hoverStateColor={hoverStateColor}
                      idleColor={idleColor}
                      index={index}
                      linkDropdownIconColor="red"
                      toggleMenu={onClickLinkWithSubMenu}
                      linkFontSizeLarge={linkFontSizeLarge}
                      linkFontSizeMedium={linkFontSizeMedium}
                      linkFontSizeSmall={linkFontSizeSmall}
                    />
                  )
                    : (
                      <Link href={navigationLink.link.href} className={css(linkStyle.link, styles.link, fontStyle)}>
                        <Span className={css(fontStyle)}>{navigationLink.link.text}</Span>
                      </Link>

                    )
                }
              </li>
            );
          })
        }
      </ul>
    </React.Fragment>
  );
};
